import React from "react";
import {
  X,
  User,
  Calendar,
  CreditCard,
  DollarSign,
  Clock,
  Hash,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CustomerDetails {
  id: string;
  name: string;
  player_id: string;
  customer_type?: string;
  email?: string | null;
  phone?: string;
}

interface AgentDetails {
  id: string;
  name: string;
  agent_id: string;
  agent_type?: string;
}

interface CashRecordDetails {
  id: string;
  type: "cash-in" | "cash-out";
  amount: number;
  date: string;
  shift: string;
  paymentType?: string;
  currency?: string;
  foreignExchangeRate?: number;
  agentId?: string | null;
  customerId?: string;
  customerDetails?: CustomerDetails;
  agentDetails?: AgentDetails;
  createdAt?: string;
  createdBy?: string;
}

interface CashRecordDetailsSidebarProps {
  record: CashRecordDetails | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CashRecordDetailsSidebar: React.FC<
  CashRecordDetailsSidebarProps
> = ({ record, isOpen, onClose }) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!record) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed right-0 top-0 h-full bg-slate-800 shadow-2xl z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "translate-x-full"}
        w-full sm:w-96 lg:w-[400px]
      `}
      >
        {/* Header */}
        <div className="bg-slate-900 p-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`
                px-3 py-1 rounded-full text-xs font-medium
                ${
                  record.type === "cash-in"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }
              `}
              >
                {record.type === "cash-in" ? "Cash In" : "Cash Out"}
              </div>
              <span className="text-white font-medium">
                #{record.id.slice(-6).toUpperCase()}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-80px)]">
          {/* Amount Section */}
          <div className="p-6 border-b border-slate-700">
            <div className="text-center">
              <p className="text-slate-400 text-sm mb-2">Transaction Amount</p>
              <p
                className={`text-3xl font-bold ${
                  record.type === "cash-in" ? "text-green-400" : "text-red-400"
                }`}
              >
                {formatCurrency(record.amount, record.currency)}
              </p>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="p-6 space-y-4">
            <h3 className="text-white font-medium mb-4">Transaction Details</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-1" />
                <div className="flex-1">
                  <p className="text-xs text-slate-400">Date</p>
                  <p className="text-white">{formatDate(record.date)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-slate-400 mt-1" />
                <div className="flex-1">
                  <p className="text-xs text-slate-400">Shift</p>
                  <p className="text-white">{record.shift}</p>
                </div>
              </div>

              {record.type === "cash-in" && record.paymentType && (
                <div className="flex items-start gap-3">
                  <CreditCard className="w-4 h-4 text-slate-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-400">Payment Type</p>
                    <p className="text-white">{record.paymentType}</p>
                  </div>
                </div>
              )}

              {record.type === "cash-in" && record.currency && (
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-slate-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-400">Currency</p>
                    <p className="text-white">{record.currency}</p>
                  </div>
                </div>
              )}

              {record.type === "cash-in" && record.foreignExchangeRate && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-4 h-4 text-slate-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-400">Exchange Rate</p>
                    <p className="text-white">{record.foreignExchangeRate}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Hash className="w-4 h-4 text-slate-400 mt-1" />
                <div className="flex-1">
                  <p className="text-xs text-slate-400">Transaction ID</p>
                  <p className="text-white font-mono text-sm">{record.id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          {(record.customerId || record.customerDetails) && (
            <div className="p-6 border-t border-slate-700 space-y-4">
              <h3 className="text-white font-medium mb-4">
                Customer Information
              </h3>

              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {record.customerDetails?.name || "Customer"}
                    </p>
                    {record.customerDetails?.player_id && (
                      <p className="text-xs text-slate-400">
                        Player ID: {record.customerDetails.player_id}
                      </p>
                    )}
                    {record.customerDetails?.phone && (
                      <p className="text-xs text-slate-400">
                        Phone: {record.customerDetails.phone}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `/customers/${record.customerId || record.customerDetails?.id}`,
                    )
                  }
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  View Profile →
                </button>
              </div>
            </div>
          )}

          {/* Agent Information */}
          {(record.agentId || record.agentDetails) && (
            <div className="p-6 border-t border-slate-700 space-y-4">
              <h3 className="text-white font-medium mb-4">Agent Information</h3>

              <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {record.agentDetails?.name || "Agent"}
                    </p>
                    {record.agentDetails?.agent_id && (
                      <p className="text-xs text-slate-400">
                        Agent Code: {record.agentDetails.agent_id}
                      </p>
                    )}
                    {record.agentDetails?.agent_type && (
                      <p className="text-xs text-slate-400">
                        Type: {record.agentDetails.agent_type}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `/agents/${record.agentId || record.agentDetails?.id}`,
                    )
                  }
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  View Profile →
                </button>
              </div>
            </div>
          )}

          {/* Metadata */}
          {record.createdAt && (
            <div className="p-6 border-t border-slate-700">
              <h3 className="text-white font-medium mb-4">
                Additional Information
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Created At</span>
                  <span className="text-slate-300">
                    {formatDate(record.createdAt)} at{" "}
                    {formatTime(record.createdAt)}
                  </span>
                </div>
                {record.createdBy && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Created By</span>
                    <span className="text-slate-300">{record.createdBy}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          {(record.customerId || record.customerDetails) && (
            <div className="p-6 border-t border-slate-700">
              <button
                onClick={() =>
                  navigate(
                    `/customers/${record.customerId || record.customerDetails?.id}`,
                  )
                }
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                View Full Customer Details
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
